import { DeleteOutlined, InboxOutlined, SaveOutlined } from '@ant-design/icons'
import { Button, Form, theme } from 'antd'
import { AxiosError } from 'axios'
import { t } from 'i18next'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import {
  BackButton,
  ConfirmDeleteModal,
  DetailsPageLayout,
  HeaderSeparator,
  PageContentDropdown,
  SectionLoadingState,
  Spacer,
  SubHeader,
} from '../../components'
import {
  LATEST_WORKSPACE_ID,
  privacyTypes,
  workspaceDocumentSetAllOwnersStatus,
  workspaceDocumentSetStatuses,
  workspaceTypes,
} from '../../constants/constants'
import { urlList } from '../../constants/urlConstant'
import useWorkspaceDetails from '../../hooks/useWorkspaceDetails'
import useWorkspaces from '../../hooks/useWorkspaces'
import { apiService, notificationService } from '../../services'
import workspaceService from '../../services/workspaceService'
import { RootState } from '../../store'
import { routePaths } from '../RootPage'

import SelectConformityDocumentOwner from './components/SelectConformityDocumentOwner'
import SelectDeviationOwner from './components/SelectDeviationOwner'
import SelectDocumentSetStatus from './components/SelectDocumentSetStatus'
import SelectWorkspacePanelType from './components/SelectWorkspacePanelType'
import { useIsMobile } from '../../hooks/useMediaQuery'
import WorkspaceTabs from './components/WorkspaceTabs'
import WorkspaceAddDocumentModal from './components/modals/WorkspaceAddDocumentModal/WorkspaceAddDocumentModal'
import WorkspaceDetailsSelectParticipantModal from './components/modals/WorkspaceDetailsAddParticipant/WorkspaceDetailsAddParticipantModal'
import WorkspaceDetailsAddStoreModal from './components/modals/WorkspaceDetailsAddStore/WorkspaceDetailsAddStoreModal'
import {
  WorkspaceConformityDocumentsPanel,
  WorkspaceDeviationsPanel,
  WorkspaceMetadataConformityCheckStudiesPanel,
  WorkspaceOverviewInfoPanel,
  WorkspaceSettingsInfoPanel,
  WorkspaceSettingsPeoplePanel,
  WorkspaceSettingsSourcePanel,
  WorkspaceSettingsStorePanel,
  WorkspaceSmartFunctionPanel,
} from './panels'

const FloatLeftContainer = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  margin-left: auto;
  order: 2;
  gap: 8px;
  align-items: center;
  white-space: normal;

  ${({ $isMobile }) =>
    $isMobile &&
    `
      width: 100%;
      justify-content: flex-end;
      margin-top: 8px;
    `}
`

export type WorkspacePanelType =
  | 'overview'
  | 'settings'
  | 'deviations'
  | 'documents'
  | 'studies'
export const workspacePanelTypes = {
  OVERVIEW: 'overview',
  SETTINGS: 'settings',
  DEVIATIONS: 'deviations',
  DOCUMENTS: 'documents',
  STUDIES: 'studies',
} as const

export type WorkspaceTab =
  | 'info'
  | 'smartFunction'
  | 'source'
  | 'people'
  | 'store'
export const workspaceTabs = {
  INFO: 'info',
  SMART_FUNCTION: 'smartFunction',
  SOURCE: 'source',
  STORE: 'store',
  PEOPLE: 'people',
} as const

// singleWorkspace is for client using iframe and are allowed to see only one workspace
interface WorkspaceDetailsPageProps {
  singleWorkspace?: boolean
}

const { useToken } = theme

const WorkspaceDetailsPage = ({
  singleWorkspace,
}: WorkspaceDetailsPageProps) => {
  const isMobile = useIsMobile()
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const { panelType, tab, workspaceId } = useParams()
  const userList = useSelector((state: RootState) => state.user.users)
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [fetchDone, setFetchDone] = useState(false)
  const [documentSetOwnerFilter, setDocumentSetOwnerFilter] = useState(
    workspaceDocumentSetAllOwnersStatus,
  )
  const [documentSetStatusFilter, setDocumentSetStatusFilter] = useState(
    workspaceDocumentSetStatuses.ALL,
  )

  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<
    string | undefined
  >(workspaceId)

  const [selectedWorkspacePanelType, setSelectedWorkspacePanelType] = useState(
    (panelType || workspacePanelTypes.OVERVIEW) as WorkspacePanelType,
  )
  const [selectedWorkspaceTab, setSelectedWorkspaceTab] =
    useState<WorkspaceTab>((tab || workspaceTabs.INFO) as WorkspaceTab)

  const {
    selectedWorkspace,
    refreshWorkspaceDetails,
    exportWorkspaceDataSourcePanelCsv,
    exportWorkspaceDataStorePanelCsv,
    exportWorkspaceParticipantsPanelCsv,
    exportWorkspaceDetailsPanelCsv,
    exportWorkspaceAlgorithmRunsListCsv,
    isDeletable,
    isUpdatable,
  } = useWorkspaceDetails({
    workspaceId: currentWorkspaceId,
    preventFetch: true,
  })

  const { loading, refreshWorkspaceList } = useWorkspaces({
    preventFetch: true,
  })

  const isWorkspaceLoading = useMemo(
    () => loading || selectedWorkspace?.id !== workspaceId,
    [loading, selectedWorkspace, workspaceId],
  )

  const defaultValues = useMemo(
    () => ({
      title: selectedWorkspace?.title,
      description: selectedWorkspace?.description,
      visibility: selectedWorkspace?.visibility,
      participants: selectedWorkspace?.participants?.map(
        (participant) => participant.id,
      ),
      config: selectedWorkspace?.config,
    }),
    [selectedWorkspace],
  )

  const participantListLength = useMemo(() => {
    if (selectedWorkspace?.visibility === privacyTypes.PRIVATE) {
      return selectedWorkspace.participants?.length || 0
    } else {
      return userList ? userList.length : 0
    }
  }, [userList, selectedWorkspace])

  useEffect(() => {
    form.setFieldsValue(defaultValues)
  }, [form, defaultValues])

  useEffect(() => {
    if (!fetchDone) {
      refreshWorkspaceDetails()
      setFetchDone(true)
    }
  }, [fetchDone, refreshWorkspaceDetails])

  useEffect(() => {
    //  if latest workspace reroute to correct url with id
    if (
      workspaceId === LATEST_WORKSPACE_ID &&
      selectedWorkspace?.id &&
      selectedWorkspace?.id !== LATEST_WORKSPACE_ID
    ) {
      const baseRoute = singleWorkspace
        ? routePaths.SINGLE_WORKSPACE_DETAILS
        : routePaths.WORKSPACE_DETAILS
      navigate(
        `${baseRoute}/${selectedWorkspace?.id}/${selectedWorkspacePanelType}/${selectedWorkspaceTab}`,
        { replace: true },
      )
    }
  }, [
    workspaceId,
    selectedWorkspace,
    selectedWorkspacePanelType,
    selectedWorkspaceTab,
    navigate,
    singleWorkspace,
  ])

  useEffect(() => {
    if (currentWorkspaceId !== workspaceId) {
      setCurrentWorkspaceId(workspaceId)
      setSelectedWorkspacePanelType(workspacePanelTypes.OVERVIEW)
      setFetchDone(false)
    }
  }, [workspaceId, selectedWorkspace, currentWorkspaceId])

  useEffect(() => {
    setSelectedWorkspacePanelType(
      (panelType || workspacePanelTypes.OVERVIEW) as WorkspacePanelType,
    )
    setSelectedWorkspaceTab((tab || workspaceTabs.INFO) as WorkspaceTab)
  }, [panelType, tab])

  const handlePanelTypeChange = useCallback(
    (value: WorkspacePanelType | undefined) => {
      setSelectedWorkspaceTab(workspaceTabs.INFO)
      const baseRoute = singleWorkspace
        ? routePaths.SINGLE_WORKSPACE_DETAILS
        : routePaths.WORKSPACE_DETAILS
      navigate(
        `${baseRoute}/${workspaceId}/${value || workspacePanelTypes.OVERVIEW}/${
          workspaceTabs.INFO
        }`,
      )
    },
    [navigate, singleWorkspace, workspaceId],
  )

  const panelIndex = useCallback(() => {
    if (selectedWorkspacePanelType === workspacePanelTypes.OVERVIEW) {
      switch (selectedWorkspaceTab) {
        case workspaceTabs.INFO:
          return 0
        case workspaceTabs.SMART_FUNCTION:
          return 2
      }
    } else if (selectedWorkspacePanelType === workspacePanelTypes.DEVIATIONS) {
      return 6
    } else if (selectedWorkspacePanelType === workspacePanelTypes.DOCUMENTS) {
      return 7
    } else if (selectedWorkspacePanelType === workspacePanelTypes.SETTINGS) {
      switch (selectedWorkspaceTab) {
        case workspaceTabs.INFO:
          return 1
        case workspaceTabs.SOURCE:
          return 3
        case workspaceTabs.STORE:
          return 4
        case workspaceTabs.PEOPLE:
          return 5
      }
    } else if (selectedWorkspacePanelType === workspacePanelTypes.STUDIES) {
      return 8
    }
    return 0
  }, [selectedWorkspacePanelType, selectedWorkspaceTab])

  const handleDelete = useCallback(() => {
    setConfirmDeleteModalOpen(true)
  }, [])

  const handleConfirmDelete = useCallback(() => {
    setDeleteLoading(true)
    if (workspaceId === undefined) {
      return
    }
    apiService
      .deleteItem(`${urlList.WORKSPACES}/${workspaceId}`, navigate)
      .then(() => {
        refreshWorkspaceList()
        notificationService.notificationSuccess({
          messageToDisplay:
            t('workspaceDetailsPage.deleteWorkspaceSuccessful') || '',
        })
        navigate(routePaths.WORKSPACE_LIST)
        setDeleteLoading(false)
      })
      .catch((error: AxiosError | Error) => {
        console.error('axios fetch error', error)
        setDeleteLoading(false)
      })
  }, [refreshWorkspaceList, navigate, workspaceId])

  const validatePrivateWorkspaceParticipants = useCallback(
    (participants: string[]): boolean => {
      if (participants.length === 0) {
        notificationService.notificationError({
          messageToDisplay: t('workspaceDetailsPage.mustHaveOneParticipant'),
        })
        return false
      }

      if (
        selectedWorkspace?.createdBy &&
        !participants.includes(selectedWorkspace?.createdBy)
      ) {
        notificationService.notificationError({
          messageToDisplay: t('workspaceDetailsPage.mustHaveOwnerParticipant'),
        })
        return false
      }

      return true
    },
    [selectedWorkspace?.createdBy],
  )

  const handleSave = useCallback(() => {
    setSaveLoading(true)
    const values = form.getFieldsValue()
    if (values.visibility === privacyTypes.PRIVATE) {
      const isValid = validatePrivateWorkspaceParticipants(values.participants)
      if (!isValid) {
        setSaveLoading(false)
        return
      }
    }
    workspaceService
      .updateWorkspaceDetails({
        workspaceId,
        ...values,
      })
      .then(() => {
        notificationService.notificationSuccess({
          messageToDisplay:
            t('workspaceDetailsPage.updateWorkspaceSuccessful') || '',
        })
        setSaveLoading(false)
        refreshWorkspaceDetails()
      })
      .catch((error: AxiosError | Error) => {
        console.error('axios fetch error', error)
        setSaveLoading(false)
      })
  }, [
    form,
    refreshWorkspaceDetails,
    workspaceId,
    setSaveLoading,
    validatePrivateWorkspaceParticipants,
  ])

  const handleWorkspaceDetailsPanelExport = () => {
    if (selectedWorkspaceTab === workspaceTabs.INFO) {
      exportWorkspaceDetailsPanelCsv(selectedWorkspace?.id || '')
    } else if (selectedWorkspaceTab === workspaceTabs.SOURCE) {
      exportWorkspaceDataSourcePanelCsv(selectedWorkspace?.id || '')
    } else if (selectedWorkspaceTab === workspaceTabs.STORE) {
      exportWorkspaceDataStorePanelCsv(selectedWorkspace?.id || '')
    } else if (selectedWorkspaceTab === workspaceTabs.PEOPLE) {
      exportWorkspaceParticipantsPanelCsv(selectedWorkspace?.id || '')
    } else if (selectedWorkspaceTab === workspaceTabs.SMART_FUNCTION) {
      exportWorkspaceAlgorithmRunsListCsv(selectedWorkspace?.id || '')
    }
  }

  const panels = useMemo(
    () => [
      <WorkspaceOverviewInfoPanel />,
      <WorkspaceSettingsInfoPanel form={form} />,
      <WorkspaceSmartFunctionPanel singleWorkspace={singleWorkspace} />,
      <WorkspaceSettingsSourcePanel />,
      <WorkspaceSettingsStorePanel />,
      <WorkspaceSettingsPeoplePanel />,
      <WorkspaceDeviationsPanel
        statusFilter={documentSetStatusFilter}
        deviationOwner={documentSetOwnerFilter}
      />,
      // TODO filters fior labQR documents
      <WorkspaceConformityDocumentsPanel
        statusFilter={documentSetStatusFilter}
        ownerFilter={documentSetOwnerFilter}
      />,
      <WorkspaceMetadataConformityCheckStudiesPanel />,
    ],
    [form, singleWorkspace, documentSetStatusFilter, documentSetOwnerFilter],
  )

  const { token } = useToken()

  const layoutBgColors = useMemo(() => {
    return {
      [workspaceTabs.INFO]: token.colorFillAlter,
      [workspaceTabs.SMART_FUNCTION]: token.colorBgLayout,
      [workspaceTabs.PEOPLE]: token.colorBgLayout,
      [workspaceTabs.SOURCE]: token.colorBgLayout,
      [workspaceTabs.STORE]: token.colorBgLayout,
    }
  }, [token])

  if (isWorkspaceLoading) {
    return <SectionLoadingState />
  }

  return (
    <DetailsPageLayout $bgColor={layoutBgColors[selectedWorkspaceTab]}>
      <SubHeader>
        {!singleWorkspace && <BackButton url={routePaths.WORKSPACE_LIST} />}
        <SelectWorkspacePanelType
          onChange={(value) => handlePanelTypeChange(value)}
          showDeviationsOption={
            selectedWorkspace?.typeCode === workspaceTypes.RISK_REL
          }
          showDocumentsOption={
            selectedWorkspace?.typeCode === workspaceTypes.OFR_QR
          }
          showStudiesOption={
            selectedWorkspace?.typeCode === workspaceTypes.CC_MCC
          }
        />
        {!!selectedWorkspace?.typeCode && // For RISK_REL, OFR_QR and CC_MCC the tabs shows only for the settings panel type
          !(
            (selectedWorkspace?.typeCode === workspaceTypes.RISK_REL &&
              (selectedWorkspacePanelType === workspacePanelTypes.OVERVIEW ||
                selectedWorkspacePanelType ===
                  workspacePanelTypes.DEVIATIONS)) ||
            (selectedWorkspace?.typeCode === workspaceTypes.OFR_QR &&
              (selectedWorkspacePanelType === workspacePanelTypes.OVERVIEW ||
                selectedWorkspacePanelType ===
                  workspacePanelTypes.DOCUMENTS)) ||
            (selectedWorkspace?.typeCode === workspaceTypes.CC_MCC &&
              (selectedWorkspacePanelType === workspacePanelTypes.OVERVIEW ||
                selectedWorkspacePanelType === workspacePanelTypes.STUDIES))
          ) && (
            <>
              <HeaderSeparator />
              <WorkspaceTabs singleWorkspace={singleWorkspace} />
            </>
          )}
        {selectedWorkspacePanelType === workspacePanelTypes.DEVIATIONS && (
          <>
            <HeaderSeparator />
            <SelectDocumentSetStatus
              onChange={(deviationStatus) =>
                setDocumentSetStatusFilter(
                  deviationStatus ?? workspaceDocumentSetStatuses.ALL,
                )
              }
              defaultValue={workspaceDocumentSetStatuses.ALL}
            />
            <SelectDeviationOwner
              onChange={(deviationOwner) =>
                setDocumentSetOwnerFilter(
                  deviationOwner ?? workspaceDocumentSetAllOwnersStatus,
                )
              }
              defaultValue={workspaceDocumentSetAllOwnersStatus}
            />
          </>
        )}
        {selectedWorkspacePanelType === workspacePanelTypes.DOCUMENTS && (
          <>
            <HeaderSeparator />
            <SelectDocumentSetStatus
              onChange={(documentSetStatus) =>
                setDocumentSetStatusFilter(
                  documentSetStatus ?? workspaceDocumentSetStatuses.ALL,
                )
              }
              defaultValue={workspaceDocumentSetStatuses.ALL}
            />
            <SelectConformityDocumentOwner
              onChange={(owner) =>
                setDocumentSetOwnerFilter(
                  owner ?? workspaceDocumentSetAllOwnersStatus,
                )
              }
              defaultValue={workspaceDocumentSetAllOwnersStatus}
            />
          </>
        )}
        {selectedWorkspacePanelType === workspacePanelTypes.SETTINGS &&
          selectedWorkspaceTab === workspaceTabs.SOURCE && (
            <>
              <InboxOutlined />
              {t('workspaceDetailsPage.totalFound', {
                count: selectedWorkspace?.dataSource ? 1 : 0, // replace with data count from api when changed
              })}
            </>
          )}
        {selectedWorkspacePanelType === workspacePanelTypes.SETTINGS &&
          selectedWorkspaceTab === workspaceTabs.STORE && (
            <>
              <InboxOutlined />
              {t('workspaceDetailsPage.totalFound', {
                count: selectedWorkspace?.dataStores?.length || 0,
              })}
              <Spacer />
              <div>
                <WorkspaceDetailsAddStoreModal />
              </div>
            </>
          )}
        {selectedWorkspacePanelType === workspacePanelTypes.SETTINGS &&
          selectedWorkspaceTab === workspaceTabs.PEOPLE && (
            <>
              <InboxOutlined />
              {t('workspaceDetailsPage.totalFound', {
                count: participantListLength,
              })}
              <Spacer />
              <>
                <WorkspaceDetailsSelectParticipantModal
                  disabled={!isUpdatable}
                />
              </>
            </>
          )}
        {selectedWorkspaceTab === workspaceTabs.INFO && (
          <>
            <Spacer />
            {selectedWorkspacePanelType === workspacePanelTypes.DOCUMENTS ? (
              <WorkspaceAddDocumentModal disabled={!isUpdatable} />
            ) : (
              <>
                {!singleWorkspace && (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={handleDelete}
                    disabled={!isDeletable}
                  >
                    {t('workspaceDetailsPage.deleteButton')}
                  </Button>
                )}
                <ConfirmDeleteModal
                  disabled={!isDeletable}
                  open={confirmDeleteModalOpen}
                  onConfirm={handleConfirmDelete}
                  onCancel={() => setConfirmDeleteModalOpen(false)}
                  loading={deleteLoading}
                >
                  {t('workspaceListPage.confirmDeleteModalContent')}
                </ConfirmDeleteModal>
              </>
            )}
            {panelType === workspacePanelTypes.SETTINGS && (
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={saveLoading}
                disabled={!isUpdatable}
              >
                {t('saveButton')}
              </Button>
            )}
          </>
        )}
        <FloatLeftContainer $isMobile={isMobile}>
          <PageContentDropdown onExport={handleWorkspaceDetailsPanelExport} />
        </FloatLeftContainer>
      </SubHeader>
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={defaultValues}
        onFinish={handleSave}
        disabled={!isUpdatable}
      >
        {panels[panelIndex()]}
      </Form>
    </DetailsPageLayout>
  )
}

export default WorkspaceDetailsPage
